import { Info } from 'lucide-react'
import { Spinner, Tooltip } from '@/components/ds2'
import { DriverRoadDistanceState, IDLE_DRIVER_ROAD_DISTANCE } from '../types/roadDistance'
import { formatMilesOutLabel, isCurrentRoadDistance, resolveMilesOut } from '../utils/roadDistance'

/** The ⓘ beside the "Miles out" header. Rendered NEXT TO the column's sort button, never inside it: inside, the
 *  tap that opens the tooltip on mobile — the only way to read it there — also re-sorted the list. */
export const NearbyDriverDistanceInfo = () => (
    <Tooltip content="≈ means direct estimate. Selecting a driver replaces it with GraphHopper truck-road miles." placement="topStart">
        <span className="inline-flex text-foreground-muted" aria-label="About miles out">
            <Info size={11} />
        </span>
    </Tooltip>
)

export const NearbyDriverDistanceCell = ({
    proximityDistanceMi,
    driverLocationFixAt,
    state = IDLE_DRIVER_ROAD_DISTANCE,
}: {
    proximityDistanceMi: number | null
    driverLocationFixAt: string | null
    state?: DriverRoadDistanceState
}) => {
    // Same resolution the Offer email and Copy Load use — same number, whatever the route is doing. Only
    // the ≈ is extra here: it is the operator's cue, and it is deliberately not in what the broker receives.
    const label = formatMilesOutLabel(resolveMilesOut(driverLocationFixAt, proximityDistanceMi, state)) ?? '—'

    if (isCurrentRoadDistance(driverLocationFixAt, state)) {
        return (
            <Tooltip content="GraphHopper truck-road miles from the driver’s last reported location to pickup." placement="right">
                <span className="whitespace-nowrap font-semibold text-foreground">{label}</span>
            </Tooltip>
        )
    }

    if (state.status === 'loading') {
        return (
            <div className="flex min-h-[18px] items-center gap-1 whitespace-nowrap font-semibold text-foreground">
                {label}
                <Tooltip content="Calculating truck-road miles…" placement="right">
                    <span className="inline-flex text-accent">
                        <Spinner size={10} />
                    </span>
                </Tooltip>
            </div>
        )
    }

    // No retry control, by product decision — the estimate already answers "how far out". Recovery is
    // re-picking the row (an `error` state falls through selectDriver's guard) or an operator edit to the
    // driver's location. Note an OPEN drawer does not re-poll: a newer fix from the driver's own phone
    // will not land until the list refetches.
    if (state.status === 'error') {
        return (
            <Tooltip content={state.message} placement="right">
                <span className="whitespace-nowrap font-semibold text-foreground">{label}</span>
            </Tooltip>
        )
    }

    return (
        <Tooltip content="Straight-line estimate. Select this driver to calculate truck-road miles." placement="right">
            <span className="whitespace-nowrap font-semibold text-foreground">{label}</span>
        </Tooltip>
    )
}
